import { useEffect, useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LEGAL } from "@/config/legal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SUPPORT_FAQS = [
  { q: "How do I reset my password?", a: "Use the \"Forgot password\" link on the login page. You'll receive an email to set a new password." },
  { q: "How can I export my data?", a: "In the app, go to Settings. Use the export options for leads and pipeline data. For full data export, contact support." },
  { q: "Who do I contact for billing?", a: "For invoices and billing questions, contact us at the support email below." },
];

export default function SupportPage() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Support | Decision Sales OS";
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const { error } = await supabase.from("support_tickets").insert({
        email: email || null,
        subject: subject.trim() || "Support request",
        message: message.trim(),
        status: "open",
      });
      if (error) throw error;
      toast({ title: "Message sent", description: "We'll get back to you soon." });
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err: unknown) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send. Try emailing us directly.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicLayout showCookieBanner={false}>
      <h1 className="text-3xl font-semibold tracking-tight mb-4">Support</h1>
      <p className="text-muted-foreground mb-8">
        FAQ and contact form. We respond within 24 hours.
      </p>

      <Tabs defaultValue="faq" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
        </TabsList>
        <TabsContent value="faq" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Frequently asked questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {SUPPORT_FAQS.map((faq, i) => (
                <div key={i}>
                  <p className="font-medium text-foreground">{faq.q}</p>
                  <p className="text-muted-foreground text-sm mt-1">{faq.a}</p>
                </div>
              ))}
              <p className="text-sm text-muted-foreground pt-4">
                Email us anytime:{" "}
                <a href={`mailto:${LEGAL.supportEmail}`} className="text-primary hover:underline">
                  {LEGAL.supportEmail}
                </a>
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="contact" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Send a message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="support-email">Email (optional)</Label>
                  <Input
                    id="support-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="support-subject">Subject</Label>
                  <Input
                    id="support-subject"
                    placeholder="Brief subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="support-message">Message</Label>
                  <textarea
                    id="support-message"
                    required
                    rows={5}
                    placeholder="Describe your question or issue..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <Button type="submit" disabled={sending}>
                  {sending ? "Sending..." : "Send message"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PublicLayout>
  );
}
